import type { UserResponse } from 'generatedTypes/user/models';
import { useEffect, useMemo, useRef } from 'react';
import { Outlet } from 'react-router-dom';

import Intercom, { update } from '@intercom/messenger-js-sdk';

import useAccount from 'hooks/useAccount';
import useFeatureFlags from 'hooks/useFeatureFlags';
import useUiVersion from 'hooks/useUiVersion';

import { APP_ID, MILLISECONDS_PER_SECOND, TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

const ChatSupport: FunctionComponent<Props> = ({ testId = TEST_ID }) => {
    const { user } = useAccount();

    const { INTERCOM_CHAT } = useFeatureFlags();
    const uiVersion = useUiVersion();
    const hasSyncedInitialVersion = useRef(false);

    const shouldLoadIntercom = useMemo(() => {
        if (user) {
            const { createdAt, email, firstName, id, intercomHmac, lastName } =
                user as UserResponse;

            return (
                firstName && lastName && email && id && intercomHmac && createdAt && INTERCOM_CHAT
            );
        }

        return false;
    }, [INTERCOM_CHAT, user]);

    if (shouldLoadIntercom) {
        const { createdAt, email, firstName, id, intercomHmac, lastName } = user as UserResponse;

        Intercom({
            app_id: APP_ID,
            created_at: Math.floor(new Date(createdAt).getTime() / MILLISECONDS_PER_SECOND),
            email: email,
            hide_default_launcher: true,
            name: `${firstName} ${lastName}`,
            ui_version: uiVersion,
            user_hash: intercomHmac ?? undefined,
            user_id: id
        });
    }

    useEffect(() => {
        if (!shouldLoadIntercom) {
            return;
        }

        if (!hasSyncedInitialVersion.current) {
            hasSyncedInitialVersion.current = true;

            return;
        }

        update({ ui_version: uiVersion });
    }, [shouldLoadIntercom, uiVersion]);

    return <Outlet data-testid={testId} />;
};

export default ChatSupport;
